import * as Sentry from "@sentry/nextjs";
import { whenReady } from "@/lib/posthog";

/**
 * Track a custom event in PostHog.
 *
 * Usage:
 *   trackEvent("button_clicked", { button: "upgrade" });
 *
 * Fails silently — analytics must never crash the app.
 */
export function trackEvent(
  name: string,
  properties?: Record<string, unknown>
): void {
  /* Not awaited by callers. PostHog is fetched on demand now, so an event raised while it
     is still downloading waits for it instead of being dropped on the floor. */
  void whenReady()
    .then((ph) => ph?.capture(name, properties))
    .catch(() => {
      // Fail silently — analytics must never crash the app.
    });
}

/**
 * Associate the current session with a known user.
 * Call after sign-in / sign-up with the authenticated user's ID.
 */
export function identifyUser(
  userId: string,
  traits?: Record<string, unknown>
): void {
  void whenReady()
    .then((ph) => ph?.identify(userId, traits))
    .catch(() => {
      // Fail silently
    });
}

/**
 * Unlink the current session from the identified user.
 * Call on sign-out so subsequent events are anonymous.
 */
export function resetUser(): void {
  void whenReady()
    .then((ph) => ph?.reset())
    .catch(() => {
      // Fail silently
    });
}

/**
 * Capture an unexpected error in Sentry with optional context.
 *
 * Use this at meaningful failure points (auth, payments, events, messaging)
 * where an exception has already been caught and you want it logged with context.
 * Do NOT call this for expected user-validation errors.
 *
 * Usage:
 *   captureError(error, { action: "checkout_initiate", eventId: "abc123" });
 */
export function captureError(
  error: unknown,
  context?: Record<string, unknown>
): void {
  try {
    Sentry.captureException(error, context ? { extra: context } : undefined);
  } catch {
    // Never let error reporting crash the app
  }
}

/**
 * Log a non-exception error message to Sentry (for warnings or unexpected states).
 *
 * Usage:
 *   captureMessage("No session returned after OTP verify", { userId });
 */
export function captureMessage(
  message: string,
  context?: Record<string, unknown>
): void {
  try {
    Sentry.captureMessage(message, context ? { extra: context } : undefined);
  } catch {
    // Fail silently
  }
}
